import { useState } from 'react';
import { pushDataLayer } from '@/lib/gtm';
import { useLanguage } from '@/context/LanguageContext';
import { useInquiryList } from '@/context/InquiryListContext';
import { submitProductInquiry } from '@/lib/supabase';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Minus, Plus, Loader2, CheckCircle, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';

const translations = {
  title: { de: 'Anfrageliste', en: 'Inquiry List', it: 'Lista Richieste' },
  empty: { de: 'Ihre Anfrageliste ist leer', en: 'Your inquiry list is empty', it: 'La tua lista richieste è vuota' },
  emptyDesc: { de: 'Fügen Sie Produkte hinzu, um eine Sammelanfrage zu senden.', en: 'Add products to send a bulk inquiry.', it: 'Aggiungi prodotti per inviare una richiesta collettiva.' },
  browseCatalog: { de: 'Katalog durchstöbern', en: 'Browse Catalog', it: 'Sfoglia Catalogo' },
  remove: { de: 'Entfernen', en: 'Remove', it: 'Rimuovi' },
  sendInquiry: { de: 'Anfrage senden für', en: 'Send inquiry for', it: 'Invia richiesta per' },
  items: { de: 'Artikel', en: 'items', it: 'articoli' },
  item: { de: 'Artikel', en: 'item', it: 'articolo' },
  sku: { de: 'Art.-Nr.', en: 'SKU', it: 'Cod.' },
  // Form
  yourDetails: { de: 'Ihre Daten', en: 'Your Details', it: 'I Tuoi Dati' },
  name: { de: 'Name', en: 'Name', it: 'Nome' },
  email: { de: 'E-Mail', en: 'Email', it: 'Email' },
  phone: { de: 'Telefon (optional)', en: 'Phone (optional)', it: 'Telefono (opzionale)' },
  message: { de: 'Nachricht (optional)', en: 'Message (optional)', it: 'Messaggio (opzionale)' },
  messagePlaceholder: { 
    de: 'Zusätzliche Details oder Fragen…', 
    en: 'Additional details or questions…', 
    it: 'Dettagli aggiuntivi o domande…' 
  },
  send: { de: 'Anfrage senden', en: 'Send Inquiry', it: 'Invia Richiesta' },
  sending: { de: 'Senden...', en: 'Sending...', it: 'Invio...' },
  successTitle: { de: 'Anfrage gesendet!', en: 'Inquiry Sent!', it: 'Richiesta Inviata!' },
  successMessage: { 
    de: 'Ihre Anfrage wurde gesendet. Wir werden Sie in Kürze kontaktieren.', 
    en: 'Your inquiry has been sent. We will contact you shortly.', 
    it: 'La tua richiesta è stata inviata. Ti contatteremo a breve.' 
  },
  continueBrowsing: { de: 'Weiter stöbern', en: 'Continue Browsing', it: 'Continua a Sfogliare' },
  selectedProducts: { de: 'Ausgewählte Produkte', en: 'Selected Products', it: 'Prodotti Selezionati' }
};

export function InquiryListDrawer() {
  const { language, getLocalizedField } = useLanguage();
  const { 
    items, 
    itemCount, 
    removeItem, 
    updateQuantity, 
    clearList,
    isDrawerOpen, 
    closeDrawer 
  } = useInquiryList();
  
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const t = (key) => translations[key]?.[language] || translations[key]?.en || key;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitBulk = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const inquiryData = {
      type: 'bulk_product_inquiry',
      customer: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message || null
      },
      products: items.map(item => ({
        name: item[`name_${language}`] || item.name_de || item.name_en,
        name_de: item.name_de,
        name_en: item.name_en,
        name_it: item.name_it,
        price: item.price,
        currency: item.currency || 'EUR',
        sku: item.sku,
        quantity: item.quantity,
        url: `${window.location.origin}/product/${item.slug}`,
        image_url: item.image_url
      })),
      total_items: itemCount,
      language,
      submitted_at: new Date().toISOString()
    };

    const result = await submitProductInquiry(inquiryData);
    
    if (result.success) {
      pushDataLayer({
        event: 'bulk_inquiry',
        total_items: itemCount,
        products: items.map(item => ({
          product_id: item.id,
          product_name: item[`name_${language}`] || item.name_de || item.name_en,
          product_sku: item.sku || undefined,
          product_price: item.price || undefined,
          product_currency: item.currency || 'EUR',
          inquiry_quantity: item.quantity,
        })),
      });
      setIsSuccess(true);
      clearList();
    }
    
    setIsSubmitting(false);
  };

  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setIsSuccess(false);
    setFormData({ name: '', email: '', phone: '', message: '' });
    if (isSuccess) {
      closeDrawer();
    }
  };

  const getItemName = (item) => {
    return item[`name_${language}`] || item.name_de || item.name_en || 'Product';
  };

  return (
    <>
      <Sheet open={isDrawerOpen} onOpenChange={closeDrawer}>
        <SheetContent 
          side="right" 
          className="w-full sm:w-[420px] bg-[#F9F8F6] border-[#E5E5E5] p-0 flex flex-col"
          data-testid="inquiry-list-drawer"
        >
          <SheetHeader className="p-6 border-b border-[#E5E5E5]">
            <SheetTitle className="font-serif text-xl text-[#2C2C2C] flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
              {t('title')} ({itemCount})
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <ShoppingBag className="h-16 w-16 text-[#E5E5E5] mb-4" strokeWidth={1} />
              <h3 className="font-serif text-lg text-[#2C2C2C] mb-2">
                {t('empty')}
              </h3>
              <p className="font-sans text-sm text-[#5D5D5D] mb-6">
                {t('emptyDesc')}
              </p>
              <Button
                asChild
                onClick={closeDrawer}
                className="rounded-full bg-[#2C2C2C] text-white px-6 py-3 h-auto hover:bg-[#4A5D4F] text-sm uppercase tracking-widest"
              >
                <Link to="/catalog">{t('browseCatalog')}</Link>
              </Button>
            </div>
          ) : (
            <>
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex gap-3 bg-white p-3 rounded-sm"
                      data-testid={`inquiry-item-${item.id}`}
                    >
                      <Link 
                        to={`/product/${item.slug}`}
                        onClick={closeDrawer}
                        className="w-16 h-16 flex-shrink-0 rounded-sm overflow-hidden bg-[#E5E5E5]"
                      >
                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={getItemName(item)}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/product/${item.slug}`}
                          onClick={closeDrawer}
                          className="font-serif text-sm text-[#2C2C2C] hover:text-[#8B5A2B] line-clamp-1"
                        >
                          {getItemName(item)}
                        </Link>
                        {item.price && item.price < 2000 && (
                          <p className="font-sans text-xs text-[#5D5D5D]">
                            {item.price.toLocaleString('de-DE', { style: 'currency', currency: item.currency })}
                          </p>
                        )}
                        {item.sku && (
                          <p className="font-sans text-xs text-[#9CA3AF]">
                            {t('sku')}: {item.sku}
                          </p>
                        )}
                        
                        {/* Quantity & Remove */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-6 w-6 rounded-full border-[#E5E5E5]"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" strokeWidth={1.5} />
                            </Button>
                            <span className="font-sans text-sm w-6 text-center">
                              {item.quantity}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-6 w-6 rounded-full border-[#E5E5E5]"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={item.quantity >= (item.maxQuantity || 99)}
                            >
                              <Plus className="h-3 w-3" strokeWidth={1.5} />
                            </Button>
                            {item.maxQuantity && item.maxQuantity < 99 && (
                              <span className="font-sans text-xs text-[#9CA3AF] ml-1">
                                /{item.maxQuantity}
                              </span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-0 text-xs text-[#5D5D5D] hover:text-red-600 hover:bg-transparent"
                            onClick={() => removeItem(item.id)}
                            data-testid={`remove-item-${item.id}`}
                          >
                            {t('remove')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Footer with Send Button */}
              <div className="p-6 border-t border-[#E5E5E5] bg-white">
                <Button
                  onClick={() => setShowBulkModal(true)}
                  className="w-full rounded-full bg-[#2C2C2C] text-white px-6 py-4 h-auto hover:bg-[#4A5D4F] text-sm uppercase tracking-widest"
                  data-testid="send-bulk-inquiry-btn"
                >
                  {t('sendInquiry')} {itemCount} {itemCount === 1 ? t('item') : t('items')}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk Inquiry Modal */}
      <Dialog open={showBulkModal} onOpenChange={handleCloseBulkModal}>
        <DialogContent className="max-w-lg bg-[#F9F8F6] border-[#E5E5E5] max-h-[90vh] overflow-y-auto">
          {isSuccess ? (
            <div className="text-center py-8" data-testid="bulk-inquiry-success">
              <CheckCircle className="h-16 w-16 text-[#4A5D4F] mx-auto mb-4" strokeWidth={1.5} />
              <DialogTitle className="font-serif text-2xl text-[#2C2C2C] mb-2">
                {t('successTitle')}
              </DialogTitle>
              <p className="font-sans text-[#5D5D5D] mb-6">
                {t('successMessage')}
              </p>
              <Button
                onClick={handleCloseBulkModal}
                className="rounded-full bg-[#2C2C2C] text-white px-8 py-3 h-auto hover:bg-[#4A5D4F] transition-colors text-sm uppercase tracking-widest"
              >
                {t('continueBrowsing')}
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl text-[#2C2C2C]">
                  {t('sendInquiry')} {itemCount} {itemCount === 1 ? t('item') : t('items')}
                </DialogTitle>
              </DialogHeader>

              {/* Products Summary */}
              <div className="py-4">
                <p className="font-sans text-xs text-[#5D5D5D] uppercase tracking-widest mb-3">
                  {t('selectedProducts')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {items.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-2 bg-white px-2 py-1 rounded-sm"
                    >
                      <div className="w-8 h-8 rounded-sm overflow-hidden bg-[#E5E5E5]">
                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={getItemName(item)}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <span className="font-sans text-xs text-[#2C2C2C] max-w-[120px] truncate">
                        {getItemName(item)} {item.quantity > 1 && `(${item.quantity}×)`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator className="bg-[#E5E5E5]" />

              {/* Form */}
              <form onSubmit={handleSubmitBulk} className="space-y-4 pt-4" data-testid="bulk-inquiry-form">
                <p className="font-sans text-xs text-[#5D5D5D] uppercase tracking-widest">
                  {t('yourDetails')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="bulk-name" className="font-sans text-xs text-[#5D5D5D]">
                      {t('name')} *
                    </Label>
                    <Input
                      id="bulk-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] h-10"
                      data-testid="bulk-input-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="bulk-email" className="font-sans text-xs text-[#5D5D5D]">
                      {t('email')} *
                    </Label>
                    <Input
                      id="bulk-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] h-10"
                      data-testid="bulk-input-email"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="bulk-phone" className="font-sans text-xs text-[#5D5D5D]">
                    {t('phone')}
                  </Label>
                  <Input
                    id="bulk-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] h-10"
                    data-testid="bulk-input-phone"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="bulk-message" className="font-sans text-xs text-[#5D5D5D]">
                    {t('message')}
                  </Label>
                  <Textarea
                    id="bulk-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={2}
                    placeholder={t('messagePlaceholder')}
                    className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] resize-none"
                    data-testid="bulk-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full bg-[#2C2C2C] text-white px-8 py-3 h-auto hover:bg-[#4A5D4F] transition-colors text-sm uppercase tracking-widest"
                  data-testid="bulk-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    t('send')
                  )}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
